import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { BRAND } from "../lib/site";
import { EliteNavMenu, MonumentalMenuTrigger } from "./EliteNavMenu";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Materials", href: "#materials" },
  { label: "Group", href: "#group" },
  { label: "Projects", href: "#projects" },
  { label: "Contact", href: "#contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [elite, setElite] = useState(false);
  const { user } = useAuth();
  const { count, setOpen: setCartOpen } = useCart();
  const navigate = useNavigate();
  const accountPath = user && user.role === "admin" ? "/admin" : "/account";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href, attempts = 0) => {
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else if (attempts < 20) {
      setTimeout(() => scrollTo(href, attempts + 1), 100);
    }
  };

  const go = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollTo(href), 120);
    } else {
      scrollTo(href);
    }
  };

  const requestQuote = () => { setOpen(false); navigate("/request-quote"); };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 w-full z-50 transition-[background,border,backdrop-filter] duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo" onClick={(e) => { e.preventDefault(); go("#top"); }} className="flex items-center gap-3 group">
          <img src="/logo-mark.png" alt="Veni Vidi Vici mark" data-testid="logo-mark" className="h-7 w-auto group-hover:scale-105 transition-transform duration-300 drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-serif text-xl tracking-tight text-[#F5F5F0]">Vinci Inc.</span>
            <span className="font-grotesk text-[8px] uppercase tracking-[0.28em] text-[#8A8A82] mt-1">Crafted for Constructions</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-5 xl:gap-6 whitespace-nowrap">
          <Link to="/shop" data-testid="nav-shop" className="relative font-grotesk text-xs uppercase tracking-[0.2em] text-[#C9C9C2] hover:text-[#F5F5F0] transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#B87333] hover:after:w-full after:transition-all after:duration-300">Shop</Link>
          <Link to="/network" data-testid="nav-network" className="relative font-grotesk text-xs uppercase tracking-[0.2em] text-[#C9C9C2] hover:text-[#F5F5F0] transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#B87333] hover:after:w-full after:transition-all after:duration-300">Network</Link>
          <Link to="/property-desk" data-testid="nav-property-desk" className="relative inline-flex items-center gap-1.5 whitespace-nowrap font-grotesk text-xs uppercase tracking-[0.2em] text-[#C9A84C] hover:text-[#F5F5F0] transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#C9A84C] hover:after:w-full after:transition-all after:duration-300">
            <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"><path d="M6 3h12l4 6-10 12L2 9l4-6z" /><path d="M2 9h20" /></svg>Property Desk
          </Link>
          <Link to="/leadership" data-testid="nav-leadership" className="relative font-grotesk text-xs uppercase tracking-[0.2em] text-[#C9C9C2] hover:text-[#F5F5F0] transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#B87333] hover:after:w-full after:transition-all after:duration-300">Leadership</Link>
          <Link to="/careers" data-testid="nav-careers" className="relative font-grotesk text-xs uppercase tracking-[0.2em] text-[#C9C9C2] hover:text-[#F5F5F0] transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#B87333] hover:after:w-full after:transition-all after:duration-300">Careers</Link>
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="hidden min-[2200px]:block relative font-grotesk text-xs uppercase tracking-[0.2em] text-[#C9C9C2] hover:text-[#F5F5F0] transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#B87333] hover:after:w-full after:transition-all after:duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-6">
          {user && user.role === "admin" && (
            <Link
              to="/admin/command"
              data-testid="header-ec-pin"
              title="Executive Command · mission control"
              className="group relative inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1
                         text-black font-grotesk text-[10px] uppercase tracking-[0.32em] font-bold
                         bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333]
                         hover:shadow-[0_0_18px_rgba(216,178,62,0.55)] transition-shadow"
              style={{ boxShadow: "0 0 10px rgba(216,178,62,0.35)" }}
            >
              <span className="w-1 h-1 rounded-full bg-black/70 animate-pulse" />
              EC
            </Link>
          )}
          {user ? (
            <Link
              to={accountPath}
              data-testid="header-account-link"
              className="font-grotesk text-xs uppercase tracking-[0.2em] text-[#C9C9C2] hover:text-[#F5F5F0] transition-colors"
            >
              {user.role === "admin" ? "Trade Desk" : "Account"}
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="header-signin-link"
              className="whitespace-nowrap font-grotesk text-xs uppercase tracking-[0.2em] text-[#C9C9C2] hover:text-[#F5F5F0] transition-colors"
            >
              Sign in
            </Link>
          )}
          <button
            data-testid="header-cart"
            onClick={() => setCartOpen(true)}
            className="relative grid place-items-center w-10 h-10 rounded-full border border-white/20 hover:border-[#B87333] transition-colors"
          >
            <ShoppingBag size={16} />
            {count > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 grid place-items-center rounded-full bg-[#B87333] text-black font-grotesk text-[10px] font-bold">{count}</span>}
          </button>
          <button
            data-testid="header-cta"
            onClick={requestQuote}
            className="inline-flex items-center gap-2 rounded-full border border-[#B87333]/60 px-5 py-2.5 font-grotesk text-xs uppercase tracking-[0.2em] text-[#F5F5F0] hover:bg-[#B87333] hover:text-black transition-colors duration-300"
          >
            Request a Quote
          </button>
          <MonumentalMenuTrigger onOpen={() => setElite(true)} />
        </div>

        <div className="md:hidden flex items-center gap-3">
          <button data-testid="header-cart-mobile" onClick={() => setCartOpen(true)} className="relative grid place-items-center w-10 h-10 rounded-full border border-white/20">
            <ShoppingBag size={16} />
            {count > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 grid place-items-center rounded-full bg-[#B87333] text-black font-grotesk text-[10px] font-bold">{count}</span>}
          </button>
          <MonumentalMenuTrigger onOpen={() => setElite(true)} />
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen((o) => !o)} className="flex flex-col gap-1.5 p-2">
            <span className={`w-6 h-px bg-[#F5F5F0] transition-transform ${open ? "translate-y-[6px] rotate-45" : ""}`} />
            <span className={`w-6 h-px bg-[#F5F5F0] transition-opacity ${open ? "opacity-0" : ""}`} />
            <span className={`w-6 h-px bg-[#F5F5F0] transition-transform ${open ? "-translate-y-[6px] -rotate-45" : ""}`} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              <button data-testid="mobile-nav-shop" onClick={() => { setOpen(false); navigate("/shop"); }} className="text-left font-serif text-2xl text-[#F5F5F0]">Shop</button>
              <button data-testid="mobile-nav-network" onClick={() => { setOpen(false); navigate("/network"); }} className="text-left font-serif text-2xl text-[#F5F5F0]">Network</button>
              <button data-testid="mobile-nav-property-desk" onClick={() => { setOpen(false); navigate("/property-desk"); }} className="text-left font-serif text-2xl text-[#C9A84C]">Property Desk</button>
              <button data-testid="mobile-nav-leadership" onClick={() => { setOpen(false); navigate("/leadership"); }} className="text-left font-serif text-2xl text-[#F5F5F0]">Leadership</button>
              <button data-testid="mobile-nav-careers" onClick={() => { setOpen(false); navigate("/careers"); }} className="text-left font-serif text-2xl text-[#F5F5F0]">Careers</button>
              {LINKS.map((l) => (
                <button key={l.href} data-testid={`mobile-nav-${l.label.toLowerCase()}`} onClick={() => go(l.href)} className="text-left font-serif text-2xl text-[#F5F5F0]">
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-auth-link"
                onClick={() => { setOpen(false); navigate(user ? accountPath : "/login"); }}
                className="text-left font-serif text-2xl text-[#F5F5F0]"
              >
                {user ? (user.role === "admin" ? "Trade Desk" : "Account") : "Sign in"}
              </button>
              <button data-testid="mobile-header-cta" onClick={requestQuote} className="mt-2 rounded-full bg-[#B87333] text-black px-5 py-3 font-grotesk text-xs uppercase tracking-[0.2em]">
                Request a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <EliteNavMenu open={elite} onClose={() => setElite(false)} />
    </motion.header>
  );
};
